import { defineSlotRecipe } from "@chakra-ui/react";

import { dialogSlotRecipe } from "./dialog-slot-recipe";
import { drawerSlotRecipe } from "./drawer-slot-recipe";
import { inputFocusInvalidStyles } from "./recipes";

const surveyLabel = {
  fontSize: "0.875rem",
  lineHeight: "133%",
  fontWeight: "normal",
  color: "gray.700"
};

export const tabsSlotRecipe = defineSlotRecipe({
  className: "chakra-tabs",
  slots: ["root", "list", "trigger", "content", "indicator"],
  base: {
    root: {
      width: "100%"
    },
    content: {
      width: "100%",
      outline: "0",
      marginInline: "0",
      _horizontal: { pt: "0" },
      _vertical: { ps: "0" }
    },
    list: {
      display: "flex",
      px: "1",
      py: "2",
      top: 0,
      zIndex: 1,
      overflowX: "auto",
      flexWrap: "nowrap",
      width: "100%",
      bg: "whiteAlpha.900",
      _dark: { bg: "gray.800" }
    },
    trigger: {
      display: "inline-flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      height: "auto",
      minW: "auto",
      flexShrink: 0,
      gap: "0",
      color: "colorPalette.600",
      "& > svg": {
        color: "colorPalette.700"
      }
    }
  },
  variants: {
    variant: {
      secondary: {
        list: {
          bg: "transparent",
          gap: "2",
          _dark: { bg: "transparent" }
        },
        trigger: {
          borderRadius: "lg",
          fontWeight: "normal",
          whiteSpace: "nowrap",
          borderWidth: "1px",
          borderStyle: "solid",
          color: "gray.800",
          borderColor: "gray.800",
          bg: "white",
          _hover: { bg: "brand.50" },
          _selected: {
            fontWeight: "semibold",
            bg: "brand.100",
            borderColor: "gray.800"
          }
        }
      },
      "soft-rounded": {
        list: {
          gap: "2"
        },
        trigger: {
          borderRadius: "lg",
          fontWeight: "normal",
          whiteSpace: "nowrap",
          borderWidth: "1px",
          borderStyle: "solid",
          color: "colorPalette.600",
          borderColor: "colorPalette.200",
          _selected: {
            fontWeight: "semibold",
            bg: "colorPalette.100",
            borderColor: "colorPalette.100"
          }
        }
      }
    },
    size: {
      md: {
        trigger: {
          py: "2",
          px: "4",
          textStyle: "md",
          fontSize: "md",
          "& > svg": {
            width: "4",
            height: "4"
          }
        }
      },
      lg: {
        trigger: {
          py: "3",
          px: "6",
          textStyle: "md",
          fontSize: "md",
          "& > svg": {
            width: "5",
            height: "5"
          }
        }
      }
    }
  },
  defaultVariants: {
    colorPalette: "gray",
    size: "md"
  }
});

export const checkboxSlotRecipe = defineSlotRecipe({
  className: "chakra-checkbox",
  slots: ["root", "control", "indicator", "label", "group"],
  base: {
    root: {
      display: "inline-flex",
      alignItems: "center",
      verticalAlign: "top",
      position: "relative",
      gap: "2",
      cursor: "pointer",
      _disabled: { cursor: "not-allowed" }
    },
    control: {
      cursor: "pointer",
      focusVisibleRing: "none",
      _focusVisible: {
        outline: "none",
        boxShadow: "outline"
      },
      _disabled: { cursor: "not-allowed" }
    }
  },
  variants: {
    size: {
      lgIconMDFont: {
        control: { w: "5", h: "5" },
        label: { fontSize: "md" }
      },
      lgIconWithLabelStyle: {
        control: { w: "5", h: "5" },
        label: surveyLabel,
        indicator: { fontSize: "xs" }
      },
      mdIconWithLabelStyle: {
        control: { w: "4", h: "4" },
        label: surveyLabel,
        indicator: { fontSize: "2xs" }
      },
      xlIconWithLabelStyle: {
        control: { w: "6", h: "6" },
        label: surveyLabel,
        indicator: { fontSize: "sm" }
      }
    }
  },
  defaultVariants: {
    colorPalette: "blue" as never
  }
});

export const radioGroupSlotRecipe = defineSlotRecipe({
  className: "chakra-radio-group",
  slots: ["root", "item", "itemControl", "itemIndicator", "itemText"],
  base: {
    item: {
      display: "inline-flex",
      alignItems: "center",
      verticalAlign: "top",
      position: "relative",
      gap: "2",
      cursor: "pointer",
      _disabled: { cursor: "not-allowed" }
    },
    itemControl: {
      cursor: "pointer",
      focusVisibleRing: "none",
      _focusVisible: {
        outline: "none",
        boxShadow: "outline"
      },
      _disabled: { cursor: "not-allowed" }
    }
  },
  variants: {
    size: {
      lgIconMDFont: {
        itemControl: { w: "5", h: "5" },
        itemText: { fontSize: "md" }
      },
      lgIconWithLabelStyle: {
        itemControl: { w: "5", h: "5" },
        itemIndicator: { fontSize: "xs" },
        itemText: surveyLabel
      },
      mdIconWithLabelStyle: {
        itemControl: { w: "4", h: "4" },
        itemIndicator: { fontSize: "2xs" },
        itemText: surveyLabel
      },
      xlIconWithLabelStyle: {
        itemControl: { w: "6", h: "6" },
        itemIndicator: { fontSize: "sm" },
        itemText: surveyLabel
      }
    }
  },
  defaultVariants: {
    colorPalette: "blue" as never
  }
});

export const fieldSlotRecipe = defineSlotRecipe({
  className: "chakra-field",
  slots: [
    "root",
    "label",
    "helperText",
    "errorText",
    "requiredIndicator",
    "errorIcon"
  ],
  base: {
    root: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      minWidth: "0",
      alignItems: "stretch",
      gap: "0"
    },
    label: {
      fontSize: "md",
      fontWeight: "medium",
      lineHeight: "normal",
      mb: 3,
      display: "block",
      color: "gray.700",
      _dark: { color: "whiteAlpha.900" }
    },
    helperText: {
      fontSize: "sm",
      lineHeight: "normal",
      color: "gray.600",
      mt: 2,
      _dark: { color: "whiteAlpha.600" }
    },
    errorText: {
      fontSize: "sm",
      fontWeight: "normal",
      lineHeight: "normal",
      color: "red.600",
      mt: 2,
      display: "flex",
      alignItems: "center",
      gap: 1,
      _dark: { color: "red.300" }
    },
    requiredIndicator: {
      marginStart: "1",
      color: "red.600",
      _dark: { color: "red.300" }
    }
  }
});

export const numberInputSlotRecipe = defineSlotRecipe({
  className: "chakra-number-input",
  slots: ["root", "input", "control", "incrementTrigger", "decrementTrigger"],
  base: {
    input: inputFocusInvalidStyles
  },
  variants: {
    variant: {
      outline: {
        input: {
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: "gray.500",
          color: "gray.800",
          ...inputFocusInvalidStyles,
          _dark: { color: "gray.100" },
          _hover: {
            borderColor: "gray.600",
            _dark: { borderColor: "whiteAlpha.400" }
          }
        }
      }
    }
  },
  defaultVariants: {
    variant: "outline"
  }
});

export const pinInputSlotRecipe = defineSlotRecipe({
  className: "chakra-pin-input",
  slots: ["root", "control", "input"],
  base: {
    input: inputFocusInvalidStyles
  },
  variants: {
    variant: {
      outline: {
        input: {
          borderColor: "gray.500",
          color: "gray.800",
          ...inputFocusInvalidStyles,
          _dark: { borderColor: "whiteAlpha.300", color: "gray.100" },
          _hover: {
            borderColor: "gray.600",
            _dark: { borderColor: "whiteAlpha.400" }
          }
        }
      }
    }
  },
  defaultVariants: {
    variant: "outline"
  }
});

export const avatarSlotRecipe = defineSlotRecipe({
  className: "chakra-avatar",
  slots: ["root", "image", "fallback"],
  base: {
    root: { bg: "gray.300" },
    fallback: { color: "gray.700" }
  },
  variants: {
    size: {
      md: {
        root: { width: "10", height: "10" },
        fallback: { fontSize: "sm" }
      },
      lg: {
        root: { width: "12", height: "12" },
        fallback: { fontSize: "md" }
      },
      xl: {
        root: { width: "14", height: "14" },
        fallback: { fontSize: "lg" }
      }
    }
  }
});

export const tooltipSlotRecipe = defineSlotRecipe({
  className: "chakra-tooltip",
  slots: ["trigger", "arrow", "arrowTip", "positioner", "content"],
  base: {
    content: {
      bg: "gray.900",
      color: "white",
      borderRadius: "base",
      fontSize: "xs",
      py: "1",
      px: "2",
      _dark: { bg: "gray.300", color: "gray.900" }
    },
    arrowTip: {
      bg: "gray.900",
      _dark: { bg: "gray.300" }
    }
  }
});

export const tableSlotRecipe = defineSlotRecipe({
  className: "chakra-table",
  slots: ["root", "header", "body", "row", "columnHeader", "cell", "footer"],
  base: {
    columnHeader: {
      fontSize: "xs",
      fontWeight: "bold",
      textTransform: "capitalize"
    },
    cell: {
      fontSize: "xs",
      wordBreak: "break-all"
    }
  }
});

const alertSizeStyles = {
  root: {
    gap: "0",
    px: "4",
    py: "3",
    textStyle: "md",
    fontSize: "md"
  },
  indicator: {
    textStyle: "md",
    width: "5",
    height: "6"
  }
};

export const alertSlotRecipe = defineSlotRecipe({
  className: "chakra-alert",
  slots: ["root", "title", "description", "indicator", "content"],
  base: {
    root: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      position: "relative",
      gap: "0",
      px: "4",
      py: "3",
      borderRadius: "md",
      color: "white",
      fontSize: "md",
      textStyle: "md"
    },
    indicator: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      color: "white",
      width: "5",
      height: "6",
      marginEnd: "3",
      _icon: { boxSize: "full" }
    },
    title: {
      fontWeight: "bold",
      color: "white",
      fontSize: "md",
      lineHeight: "6",
      marginEnd: "2"
    },
    description: {
      display: "block",
      color: "white",
      fontSize: "md",
      lineHeight: "6"
    },
    content: {
      display: "flex",
      flex: "1",
      gap: "0",
      flexDirection: "column"
    }
  },
  variants: {
    status: {
      info: {
        root: { bg: "feedback.info", colorPalette: "blue" }
      },
      success: {
        root: { bg: "feedback.success", colorPalette: "green" }
      },
      warning: {
        root: { bg: "feedback.warning", colorPalette: "orange" }
      },
      error: {
        root: { bg: "feedback.error", colorPalette: "red" }
      }
    },
    variant: {
      solid: {},
      subtle: {},
      surface: {},
      outline: {}
    },
    size: {
      sm: alertSizeStyles,
      md: alertSizeStyles,
      lg: alertSizeStyles
    },
    inline: {
      true: {
        content: {
          display: "inline-flex",
          flexDirection: "row",
          alignItems: "center"
        }
      },
      false: {
        content: {
          display: "flex",
          flexDirection: "column"
        }
      }
    }
  },
  compoundVariants: [
    {
      variant: "solid",
      status: "info",
      css: { root: { bg: "feedback.info" } }
    },
    {
      variant: "solid",
      status: "success",
      css: { root: { bg: "feedback.success" } }
    },
    {
      variant: "solid",
      status: "warning",
      css: { root: { bg: "feedback.warning" } }
    },
    {
      variant: "solid",
      status: "error",
      css: { root: { bg: "feedback.error" } }
    }
  ],
  defaultVariants: {
    variant: "solid",
    size: "md",
    inline: false
  }
});

export const toastSlotRecipe = defineSlotRecipe({
  className: "chakra-toast",
  slots: [
    "root",
    "title",
    "description",
    "indicator",
    "actionTrigger",
    "closeTrigger"
  ],
  base: {
    root: {
      display: "flex",
      alignItems: "flex-start",
      position: "relative",
      color: "white",
      borderRadius: "md",
      boxShadow: "lg",
      px: 4,
      py: 3,
      minW: { base: "288px", lg: "448px" },
      w: { base: "100%", md: "auto" },
      mb: { base: "80px", xl: "20px" },
      gap: 3
    },
    title: {
      color: "white",
      fontSize: "md",
      fontWeight: "bold",
      lineHeight: "6"
    },
    description: {
      display: "block",
      color: "white",
      fontSize: "sm",
      lineHeight: "short",
      opacity: 1
    },
    indicator: {
      flexShrink: 0,
      color: "white",
      width: "5",
      height: "6",
      _icon: { boxSize: "full" }
    },
    closeTrigger: {
      position: "relative",
      top: "auto",
      insetEnd: "auto",
      flexShrink: 0,
      marginInlineStart: "auto",
      alignSelf: "flex-start",
      w: "24px",
      h: "24px",
      me: 1,
      color: "whiteAlpha.900",
      _icon: { boxSize: "1em" }
    }
  },
  variants: {
    type: {
      info: { root: { bg: "feedback.info" } },
      success: { root: { bg: "feedback.success" } },
      warning: { root: { bg: "feedback.warning" } },
      error: { root: { bg: "feedback.error" } },
      loading: { root: { bg: "feedback.info" } }
    }
  }
});

export const tagSlotRecipe = defineSlotRecipe({
  className: "chakra-tag",
  slots: ["root", "label", "closeTrigger", "startElement", "endElement"],
  base: {
    root: {
      borderRadius: "md",
      fontWeight: "medium"
    },
    label: {
      lineHeight: "inherit"
    }
  },
  variants: {
    size: {
      sm: {
        root: { minH: "5", h: "auto", fontSize: "xs", px: "2" }
      },
      md: {
        root: { minH: "6", h: "auto", fontSize: "sm", px: "2" }
      },
      lg: {
        root: { minH: "7", h: "auto", fontSize: "sm", px: "2.5" }
      },
      xl: {
        root: { minH: "8", h: "auto", fontSize: "md", px: "3" }
      }
    }
  },
  defaultVariants: {
    size: "md",
    colorPalette: "gray"
  }
});

export const listSlotRecipe = defineSlotRecipe({
  className: "chakra-list",
  slots: ["root", "item", "indicator"],
  base: {
    root: {
      display: "block",
      gap: "0",
      flexDirection: "unset"
    },
    item: {
      whiteSpace: "normal",
      display: "list-item",
      verticalAlign: "top"
    },
    indicator: {
      marginEnd: "2",
      minHeight: "1lh",
      flexShrink: 0,
      display: "inline-block",
      verticalAlign: "middle"
    }
  },
  variants: {
    variant: {
      marker: {
        root: {
          listStyle: "revert"
        },
        item: {
          _marker: {
            color: "fg.subtle"
          }
        }
      },
      plain: {
        item: {
          display: "list-item",
          alignItems: "unset"
        }
      }
    }
  },
  defaultVariants: {
    variant: "plain"
  }
});

export const slotRecipes = {
  dialog: dialogSlotRecipe,
  drawer: drawerSlotRecipe,
  tabs: tabsSlotRecipe,
  checkbox: checkboxSlotRecipe,
  radioGroup: radioGroupSlotRecipe,
  field: fieldSlotRecipe,
  numberInput: numberInputSlotRecipe,
  pinInput: pinInputSlotRecipe,
  avatar: avatarSlotRecipe,
  tooltip: tooltipSlotRecipe,
  table: tableSlotRecipe,
  alert: alertSlotRecipe,
  toast: toastSlotRecipe,
  tag: tagSlotRecipe,
  list: listSlotRecipe
};
